import React, { useMemo, useState } from "react";
import { Box, Typography, Grid, Button, useTheme, CircularProgress, Stack } from "@mui/material";
import AsyncUserSelect from "../components/AsyncUserSelect";
import SelectedUsersList from "../components/SelectedUsersList";
import AssignResultsModal from "../components/AssignResultsPanel";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import useSearchUsers from "../hooks/useSearchUsers";
import useBulkCreateAssignapps from "../hooks/useBulkCreateAssignapps";
import { useNavigate } from "react-router-dom";
import { routes } from "../routes/routes";
import AssignmentPicker, { buildAssignPayload } from "../components/AssignmentPicker";

import useUploadExcelForAssignapps from "../hooks/useImportAssignappsFromExcel";
import BulkLoadModal from "../components/Modal/BulkLoadModal";
import DescriptionIcon from "@mui/icons-material/Description";

import { normalizeBulkCreate, buildSummary } from "../utils/assignapps";

const AssignmentCreatePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { createAssignapps, loading: creating } = useBulkCreateAssignapps();
  const { searchUsers } = useSearchUsers();
  const { uploadExcel, loading: importing } = useUploadExcelForAssignapps();

  const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_asignaciones_plantilla.xlsx`;

  // Estado del formulario
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pickerValue, setPickerValue] = useState({
    appId: "",
    activationStart: "",
    activationEnd: "",
    selectedQuestionnaireItems: [],
  });

  const [snackbar, setSnackbar] = useState({ open: false, title: "Error", message: "" });
  const [submitSummary, setSubmitSummary] = useState(null);
  const [openSummary, setOpenSummary] = useState(false);

  const [openImportModal, setOpenImportModal] = useState(false);

  const userIds = useMemo(() => selectedUsers.map((u) => u.id), [selectedUsers]);
  // Validación de ventana
  const windowError = (() => {
    const startTime = pickerValue.activationStart && new Date(pickerValue.activationStart).getTime();
    const endTime = pickerValue.activationEnd && new Date(pickerValue.activationEnd).getTime();
    if (startTime && endTime && endTime <= startTime) {
      return "Ventana inválida: el fin debe ser mayor que el inicio.";
    }
    return "";
  })();

  const hasAllFields =
    userIds.length > 0 &&
    !!pickerValue.appId &&
    (pickerValue.selectedQuestionnaireItems?.length || 0) > 0 &&
    !!pickerValue.activationStart &&
    !!pickerValue.activationEnd &&
    !windowError;

  const isSubmitDisabled = creating || !hasAllFields;

  const pickerErrorHook = (msg) => {
    setSnackbar({ open: true, title: "Validación", message: msg });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (windowError) {
      setSnackbar({ open: true, title: "Validación", message: windowError });
      return;
    }
    if ((pickerValue.selectedQuestionnaireItems?.length || 0) === 0) {
      setSnackbar({ open: true, title: "Validación", message: "Debe agregar al menos un cuestionario." });
      return;
    }

    const body = buildAssignPayload({
      userIds,
      appId: pickerValue.appId,
      selectedQuestionnaireItems: pickerValue.selectedQuestionnaireItems,
      activationStart: pickerValue.activationStart,
      activationEnd: pickerValue.activationEnd,
    });

    const result = await createAssignapps(body);
    if (!result.ok) {
      setSnackbar({
        open: true,
        title: "Error",
        message: result.errorMessage || "Error inesperado creando asignaciones.",
      });
      return;
    }

    const normalized = normalizeBulkCreate(result.data);
    const summary = buildSummary(normalized, { selectedUsers });
    setSubmitSummary(summary);
    setOpenSummary(true);

    // Reset
    setPickerValue({
      appId: "",
      activationStart: "",
      activationEnd: "",
      selectedQuestionnaireItems: [],
    });
  };

  const isAggregate = (data) => data && typeof data === "object" && ("created" in data || "updated" in data || "errors" in data);

  const handleImportFile = async (file) => {
    const { ok, data, status, errorMessage } = await uploadExcel(file);

    if (ok || (status === 422 && isAggregate(data))) {
      const summary = buildSummary(data);
      setSubmitSummary(summary);
      setOpenSummary(true);
      return true;
    }

    setSnackbar({
      open: true,
      title: "Error",
      message: (data && (data.message || data.error)) || errorMessage || "Error al importar asignaciones.",
    });
    return false;
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 240px)" px={2}>
      <Box
        maxWidth="760px"
        width="100%"
        p={{ xs: 2, sm: 4 }}
        bgcolor="white"
        borderRadius={3}
        boxShadow={theme.shadows[3]}
        sx={{ border: `1px solid ${theme.palette.grey[200]}` }}
      >
        <Typography variant="h5" fontWeight={600} mb={4}>
          Crear nueva asignación
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Usuarios */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" mb={3} color="text.secondary">
                Seleccione a los usuarios:
              </Typography>

              <AsyncUserSelect
                value={selectedUsers}
                onChange={(usersArray) => setSelectedUsers(usersArray)}
                searchUsers={searchUsers}
                label="Usuarios"
                placeholder="Buscar por email o documento..."
                minChars={2}
                pageSize={10}
                limitTags={2}
              />

              <SelectedUsersList
                users={selectedUsers}
                onRemove={(idToRemove) => {
                  const next = selectedUsers.filter((user) => user.id !== idToRemove);
                  setSelectedUsers(next);
                }}
                onClear={() => setSelectedUsers([])}
              />
            </Grid>

            <Grid item xs={12}>
              <AssignmentPicker value={pickerValue} onChange={setPickerValue} busy={creating} errorHook={pickerErrorHook} />
            </Grid>

            {/* Botones */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="center" mt={3}>
                {/* Botón volver */}
                <Button variant="contained" size="large" onClick={() => navigate(routes.usuarios)} sx={{ textTransform: "none", borderRadius: 2 }}>
                  Volver
                </Button>
                {/* Botón importar desde Excel */}
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setOpenImportModal(true)}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                  startIcon={importing ? <CircularProgress size={18} color="inherit" /> : null}
                  disabled={importing}
                >
                  {importing ? "Importando…" : "Importar desde Excel"}
                </Button>
                {/* Botón crear nuevo usuario */}
                <Button variant="outlined" size="large" onClick={() => navigate(routes.usuarioCrear)} sx={{ textTransform: "none", borderRadius: 2 }}>
                  Crear nuevo usuario
                </Button>
                {/* Botón guardar */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ textTransform: "none", borderRadius: 2 }}
                  disabled={isSubmitDisabled}
                  startIcon={creating && <CircularProgress size={20} color="inherit" />}
                >
                  {creating ? "Creando…" : "Crear asignación"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>

        {/* Modal con el informe */}
        <AssignResultsModal open={openSummary} onClose={() => setOpenSummary(false)} summary={submitSummary} />

        {/* Snackbar de errores */}
        <ErrorSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          title={snackbar.title}
          message={snackbar.message}
        />
      </Box>

      {/* Modal Importar Excel */}
      <BulkLoadModal
        open={openImportModal}
        onClose={() => setOpenImportModal(false)}
        onSelectFile={handleImportFile}
        title="Importar asignaciones por Excel"
        description="Puede descargar la plantilla requerida para la creación de asignaciones y cargarla desde su equipo."
        template={{ url: TEMPLATE_URL, label: "Descargar plantilla" }}
        accept=".xlsx,.xls"
        maxSizeMB={10}
        uploadButtonLabel="Subir y crear"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={["Fechas en formato: YYYY-MM-DD HH:mm"]}
        closeOnSuccess={true}
      />
    </Box>
  );
};

export default AssignmentCreatePage;
